import { useMemo } from "react"
import { Payment, PaymentStatus } from "@/types/payment"
import { useLocation } from "wouter"
import { useFindAll } from "../queries/relacao-pagamentos-uses"
import { RelacaoPagamentosGetResponse } from "@/services/end-points/payments/payments.type"
import { useProjetoContext } from "@/context/projeto"

const parseStatus = (status: string | undefined | null): PaymentStatus => {
  if (!status) return "Solicitado"
  const s = status.toLowerCase()
  if (s === "aprovado") return "Aprovado"
  if (s === "pago") return "Pago"
  if (s === "cancelado") return "Cancelado"
  return "Solicitado"
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleDateString("pt-BR")
}

const transformBudgetItemToPayment = (item: RelacaoPagamentosGetResponse, index: number): Payment => ({
  id: String(item.id),
  number: `1.${index + 1}`,
  creditorName: item.staffContract.staffUser.person.name || "Sem descrição",
  paymentDate: formatDate(item.due_date),
  fiscalDocType: item.emission_type || "Nota Fiscal",
  fiscalDocNumber: item.emission_number || "-",
  emissionDate: formatDate(item.emission_date),
  amount: item.amount,
  budget_step_id: (item.budgetItem as { id_buget_step: number })?.id_buget_step,
  budget_item_id: String(item.budget_item_id || ""),
  status: parseStatus(item.status),
  id_staff_contract: item.id_staff_contract,
  fiscal_note_url: item.fiscal_note_url || "",
  transactionType: "Saída",
})

/**
 * Hook para gerenciar a relação de pagamentos
 * Encapsula busca de dados, transformações e handlers de CRUD
 */
export function useRelacaoPagamentos() {
  const [_, navigate] = useLocation()

  const { projetos, projetoSelecionado } = useProjetoContext()
  const selectedProjectId = projetoSelecionado?.id || 0

  const { data: paymentsList, isLoading } = useFindAll(selectedProjectId)

  const payments: Payment[] = useMemo(() => {
    if (!paymentsList?.length) return []
    return paymentsList.map((item, index: number) =>
      transformBudgetItemToPayment(item, index)
    )
  }, [paymentsList])

  return {
    projetos,
    payments,
    projetoSelecionado,
    isLoading,
    navigate
  }
}
